import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import PhotoUploader from '../components/PhotoUploader';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, User, Camera, Shield, CheckCircle, Truck, Banknote, Zap, Check, AlertCircle, Loader2 } from 'lucide-react';
import EmailVerificationPrompt from '../components/EmailVerificationPrompt';
import { calculateConfiguredPlatformFee, MINIMUM_PLATFORM_FEE, usePlatformConfig } from '../context/PlatformConfigContext';
import { getDefaultMinimumTransactionAmount, getPayoutScheduleMessage } from '../utils/payoutSchedule';
import { getTransactionLimitCopy } from '../components/transactionState';
import { trackTransactionCreated } from '../utils/analytics';

function parseErrorMessage(error) {
  const detail = error.response?.data?.detail;
  if (!detail) return 'An error occurred';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return 'An error occurred';
}

// Launch default is OFF — Courier Guy has not completed real end-to-end
// certification yet. Absence of the env var must NOT silently enable it.
const COURIER_ENABLED = process.env.REACT_APP_COURIER_ENABLED === 'true';

function roundMoney(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

// Plain-English descriptions for each Courier Guy service level, so users can pick
// the right one. Keyed by the lowercased service-level name returned by ShipLogic.
const COURIER_LEVEL_DESCRIPTIONS = {
  'local sameday flyer': 'Small items — same day delivery (envelope/bag sized)',
  'local overnight flyer': 'Small items — next day delivery (envelope/bag sized)',
  'economy': 'Standard parcels — delivered in 2-3 days',
  'local same day economy': 'Standard parcels — same day delivery',
  'local sameday express': 'Any parcel — delivered within 90 minutes (premium)',
};

// TrustTrade does NOT know the real payment-provider processing fee before the
// buyer actually pays: confirmed directly against TradeSafe's API — the
// checkoutLink mutation returns only a URL (no fee/calculation field exists on
// it), and no deposit (where the real processingFee eventually appears) exists
// until a payment is attempted. A prior real production test (2026-10-04)
// quoted a flat-2.5% guess of R522.50 for a R500 item; the actual hosted
// checkout charged R530.41 — proof a flat percentage cannot safely be shown to
// a customer as if it were the known figure. So this screen only ever computes
// and displays TrustTrade's OWN fee (which we do set, and is exact); any side
// whose total is affected by the provider's processing fee is never quoted as
// a precise number pre-payment — see tsFeeApplies() below.

function tsFeeApplies(feeAllocation, side) {
  // Mirrors splitTrustTradeFee()'s allocation buckets: which side(s) carry any
  // provider processing cost at all for a given fee_allocation, without
  // guessing its rand amount.
  const alloc = (feeAllocation || 'BUYER').toUpperCase();
  if (['SELLER', 'SELLER_AGENT'].includes(alloc)) return side === 'seller';
  if (['BUYER_SELLER', 'SPLIT', 'SPLIT_AGENT', 'BUYER_SELLER_AGENT'].includes(alloc)) return true;
  return side === 'buyer';
}

function splitTrustTradeFee(totalFee, feeAllocation) {
  const fee = roundMoney(totalFee);
  const alloc = (feeAllocation || 'BUYER').toUpperCase();

  if (['SELLER', 'SELLER_AGENT'].includes(alloc)) {
    return { buyerFee: 0, sellerFee: fee };
  }
  if (['BUYER_SELLER', 'SPLIT', 'SPLIT_AGENT', 'BUYER_SELLER_AGENT'].includes(alloc)) {
    const buyerFee = roundMoney(fee / 2);
    return { buyerFee, sellerFee: roundMoney(fee - buyerFee) };
  }
  return { buyerFee: fee, sellerFee: 0 };
}

const DELIVERY_METHOD_LABELS = {
  courier: 'Courier Guy',
  bank_deposit: 'In-Person Meeting',
  digital: 'Digital Delivery',
};

const ITEM_CATEGORIES = [
  { value: 'electronics', label: 'Electronics' },
  { value: 'vehicles', label: 'Vehicles' },
  { value: 'furniture', label: 'Furniture' },
  { value: 'clothing', label: 'Clothing & Accessories' },
  { value: 'sports', label: 'Sports & Outdoor' },
  { value: 'services', label: 'Services' },
  { value: 'other', label: 'Other' },
];

const S = {
  page: { maxWidth: 640, margin: '0 auto' },
  card: {
    background: '#16181B',
    borderRadius: 14,
    border: '1px solid #242830',
    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
    padding: '22px 24px',
    marginBottom: 14,
  },
  label: {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    color: '#9AA4B2',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    height: 40,
    padding: '0 12px',
    borderRadius: 8,
    border: '1px solid #242830',
    fontSize: 13,
    color: '#F5F7FA',
    background: '#16181B',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    transition: 'border-color 0.15s',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #242830',
    fontSize: 13,
    color: '#F5F7FA',
    background: '#16181B',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: 80,
    transition: 'border-color 0.15s',
  },
  select: {
    width: '100%',
    height: 40,
    padding: '0 12px',
    borderRadius: 8,
    border: '1px solid #242830',
    fontSize: 13,
    color: '#F5F7FA',
    background: '#16181B',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: 32,
  },
  btnPrimary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    width: '100%',
    height: 44,
    borderRadius: 10,
    border: 'none',
    background: '#2F9BFF', color: '#061421',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.15s',
    fontFamily: 'inherit',
  },
  btnSuccess: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    width: '100%',
    height: 44,
    borderRadius: 10,
    border: 'none',
    background: '#3DD194', color: '#061421',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.15s',
    fontFamily: 'inherit',
  },
  btnGhost: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 12px',
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    color: '#9AA4B2',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    marginBottom: 16,
  },
};

function NewTransaction() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [role, setRole] = useState('seller');
  const [photos, setPhotos] = useState([]);
  const [formData, setFormData] = useState({
    buyer_name: '',
    buyer_email: '',
    seller_name: '',
    seller_email: '',
    item_description: '',
    item_category: '',
    item_condition: '',
    known_issues: '',
    item_price: '',
    fee_allocation: 'BUYER',
    // Require an explicit delivery choice; courier remains gated for launch.
    delivery_method: '',
  });
  const [confirmations, setConfirmations] = useState({
    buyer_details: false,
    seller_details: false,
    item_accuracy: false,
  });
  const [improveLoading, setImproveLoading] = useState(false);
  const [courierForm, setCourierForm] = useState({
    collection_preference: 'collection', // 'collection' = Courier Guy collects | 'dropoff' = seller drops off
    pickup_street: '', pickup_area: '', pickup_city: '', pickup_code: '',
    delivery_street: '', delivery_area: '', delivery_city: '', delivery_code: '',
    weight: '2', length: '30', width: '20', height: '15',
  });
  const [courierQuotes, setCourierQuotes] = useState([]);
  const [courierLoading, setCourierLoading] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
  const { config: platformConfig } = usePlatformConfig();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    if (role === 'buyer') {
      setFormData(prev => ({
        ...prev,
        buyer_name: user.name,
        buyer_email: user.email,
        // When switching to buyer, preserve what was typed as "other party" (buyer fields)
        // by moving it into the seller fields (now the other party).
        seller_name: prev.seller_name === user.name || !prev.seller_name ? prev.buyer_name : prev.seller_name,
        seller_email: prev.seller_email === user.email || !prev.seller_email ? prev.buyer_email : prev.seller_email,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        seller_name: user.name,
        seller_email: user.email,
        // When switching to seller, preserve what was typed as "other party" (seller fields)
        // by moving it into the buyer fields (now the other party).
        buyer_name: prev.buyer_name === user.name || !prev.buyer_name ? prev.seller_name : prev.buyer_name,
        buyer_email: prev.buyer_email === user.email || !prev.buyer_email ? prev.seller_email : prev.buyer_email,
      }));
    }
  }, [role, user]);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      navigate('/');
    }
  };

  const hasBankingDetails = Boolean(
    user?.banking_details_completed &&
    user?.banking_details?.bank_name &&
    user?.banking_details?.account_number
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImproveDescription = async () => {
    if (!formData.item_description || improveLoading) return;
    setImproveLoading(true);
    try {
      const res = await api.post('/ai/improve-description', {
        description: formData.item_description,
        item_price: parseFloat(formData.item_price) || 0,
        delivery_method: formData.delivery_method || undefined,
      });
      setFormData(prev => ({ ...prev, item_description: res.data.improved }));
      toast.success('Description improved!');
    } catch {
      toast.error('Could not improve description. Please try again.');
    } finally {
      setImproveLoading(false);
    }
  };

  const handleGetQuote = async () => {
    setCourierLoading(true);
    setCourierQuotes([]);
    setSelectedQuote(null);
    try {
      const res = await api.post('/courier/quote', {
        // For drop-off the seller brings the parcel to a Courier Guy point, so the exact
        // street isn't required — fall back to a label while keeping area/city/code for rating.
        pickup_address: { street_address: courierForm.pickup_street || (isDropoff ? 'Courier Guy drop-off point' : ''), local_area: courierForm.pickup_area, city: courierForm.pickup_city, code: courierForm.pickup_code, country: 'ZA', type: 'residential' },
        delivery_address: { street_address: courierForm.delivery_street, local_area: courierForm.delivery_area, city: courierForm.delivery_city, code: courierForm.delivery_code, country: 'ZA', type: 'residential' },
        parcel: { submitted_weight_kg: parseFloat(courierForm.weight) || 1, submitted_length_cm: parseFloat(courierForm.length) || 10, submitted_width_cm: parseFloat(courierForm.width) || 10, submitted_height_cm: parseFloat(courierForm.height) || 10 },
      });
      const rates = res.data.rates || [];
      setCourierQuotes(rates);
      if (rates.length === 0) toast.info('No courier options available for these addresses.');
      else toast.success(`${rates.length} delivery option${rates.length !== 1 ? 's' : ''} found`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 502 || status === 503 || !status) {
        toast.error('Courier booking unavailable right now. Please choose a different delivery method.');
      } else if (status === 422) {
        toast.error('Invalid address or parcel details. Please check and try again.');
      } else {
        toast.error('Could not get courier quote. Please check the addresses and try again.');
      }
    } finally {
      setCourierLoading(false);
    }
  };

  const itemPrice = parseFloat(formData.item_price) || 0;
  const minimumTransactionAmount = getDefaultMinimumTransactionAmount(platformConfig);
  const maximumTransactionAmount = Number(platformConfig.maximum_transaction || 0);
  const payoutSchedule = useMemo(() => getPayoutScheduleMessage(new Date(), platformConfig), [platformConfig]);
  const feeAlloc = formData.fee_allocation || 'BUYER';
  const isCourierDelivery = formData.delivery_method === 'courier';
  const isDropoff = courierForm.collection_preference === 'dropoff';
  // Coerce to a number — ShipLogic can return the rate as a string, which would
  // otherwise string-concatenate into the totals instead of adding numerically.
  const courierFee = isCourierDelivery && selectedQuote ? (Number(selectedQuote?.price ?? selectedQuote?.rate ?? selectedQuote?.total ?? 0) || 0) : 0;
  const courierTotal = courierFee;
  const platformFee = roundMoney(calculateConfiguredPlatformFee(itemPrice, platformConfig.platform_fee_percent));
  const trusttradeFee = platformFee; // alias kept for any legacy references
  const { buyerFee: buyerFeeContrib, sellerFee: sellerFeeContrib } = splitTrustTradeFee(platformFee, feeAlloc);
  // Whether EACH side carries any provider processing cost under the current
  // allocation — a yes/no fact we DO know, not the rand amount, which we don't
  // until the provider confirms it at checkout/payout.
  const buyerHasProviderFee = tsFeeApplies(feeAlloc, 'buyer');
  const sellerHasProviderFee = tsFeeApplies(feeAlloc, 'seller');
  // "Before provider fee" figures: item value + courier (pass-through, exact)
  // + TrustTrade's own fee (exact, we set it) — deliberately excludes any
  // provider-processing guess.
  const buyerPaysBeforeProvider = roundMoney(itemPrice + courierTotal + buyerFeeContrib);
  const sellerReceivesBeforeProvider = roundMoney(itemPrice - sellerFeeContrib);
  const feePreview = (allocation) => {
    const { buyerFee, sellerFee } = splitTrustTradeFee(platformFee, allocation);
    return {
      buyerPaysBeforeProvider: roundMoney(itemPrice + courierTotal + buyerFee),
      sellerReceivesBeforeProvider: roundMoney(itemPrice - sellerFee),
      buyerHasProviderFee: tsFeeApplies(allocation, 'buyer'),
      sellerHasProviderFee: tsFeeApplies(allocation, 'seller'),
    };
  };
  const describeFeePreview = (allocation) => {
    const p = feePreview(allocation);
    const buyerPart = `Buyer pays R${p.buyerPaysBeforeProvider.toFixed(2)}${p.buyerHasProviderFee ? ' + provider fee (shown on the payment screen before you pay)' : ''}`;
    const sellerPart = p.sellerHasProviderFee
      ? `seller receives up to R${p.sellerReceivesBeforeProvider.toFixed(2)} (minus provider fee, confirmed at payout)`
      : `seller receives R${p.sellerReceivesBeforeProvider.toFixed(2)}`;
    return `${buyerPart} - ${sellerPart}`;
  };

  const canProceedStep1 = role && (
    role === 'buyer'
      ? (formData.seller_name && formData.seller_email)
      : (formData.buyer_name && formData.buyer_email)
  );

  const amountTooLow = itemPrice > 0 && itemPrice < minimumTransactionAmount;
  const amountTooHigh = maximumTransactionAmount > 0 && itemPrice > maximumTransactionAmount;
  const canProceedStep2 = formData.item_description && formData.item_category &&
    formData.item_condition && formData.delivery_method && itemPrice >= minimumTransactionAmount && !amountTooHigh &&
    // Courier needs a selected rate so the delivery cost is captured + shown in the summary.
    (!isCourierDelivery || !!selectedQuote);
  const amountError = amountTooLow
    ? `Minimum transaction amount is R${minimumTransactionAmount.toFixed(0)} to cover processing fees.`
    : amountTooHigh
      ? `Maximum transaction amount is R${maximumTransactionAmount.toLocaleString('en-ZA')}.`
      : '';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user?.phone_verified) {
      toast.error('Verify your phone number to continue.');
      navigate('/verify/phone');
      return;
    }

    if (!formData.delivery_method) {
      toast.error('Choose a delivery method before creating the transaction.');
      setStep(2);
      return;
    }

    if (!confirmations.buyer_details || !confirmations.seller_details || !confirmations.item_accuracy) {
      toast.error('Please tick all confirmation checkboxes');
      return;
    }
    if (itemPrice < minimumTransactionAmount) {
      toast.error(`Minimum transaction amount is R${minimumTransactionAmount.toFixed(0)} to cover processing fees.`);
      return;
    }
    if (maximumTransactionAmount > 0 && itemPrice > maximumTransactionAmount) {
      toast.error(`Maximum transaction amount is R${maximumTransactionAmount.toLocaleString('en-ZA')}.`);
      return;
    }

    setLoading(true);
    try {
      const transactionResponse = await api.post('/transactions', {
        creator_role: role,
        buyer_name: role === 'buyer' ? user.name : formData.buyer_name,
        buyer_email: role === 'buyer' ? user.email : formData.buyer_email,
        seller_name: role === 'seller' ? user.name : formData.seller_name,
        seller_email: role === 'seller' ? user.email : formData.seller_email,
        item_description: formData.item_description,
        item_category: formData.item_category,
        item_condition: formData.item_condition,
        known_issues: formData.known_issues || 'None',
        item_price: itemPrice,
        fee_allocation: formData.fee_allocation,
        delivery_method: formData.delivery_method,
        ...(isCourierDelivery && selectedQuote ? {
          courier_quote_id: selectedQuote?.service_level?.code || selectedQuote?.id || selectedQuote?.code || '',
          // ShipLogic requires the service_level id (not just the code) when booking.
          courier_service_level_id: selectedQuote?.service_level?.id ?? null,
          courier_service_name: selectedQuote?.service_level?.name || selectedQuote?.name || '',
          courier_fee: courierFee,
          courier_handling_fee: 0,
          courier_collection_preference: courierForm.collection_preference,
          // Full pickup/delivery/parcel details so the backend can auto-book the
          // shipment once the escrow is funded (no caller needs to re-enter them).
          courier_details: {
            pickup_address: { street_address: courierForm.pickup_street || (isDropoff ? 'Courier Guy drop-off point' : ''), local_area: courierForm.pickup_area, city: courierForm.pickup_city, code: courierForm.pickup_code, country: 'ZA', type: 'residential' },
            delivery_address: { street_address: courierForm.delivery_street, local_area: courierForm.delivery_area, city: courierForm.delivery_city, code: courierForm.delivery_code, country: 'ZA', type: 'residential' },
            parcel: { submitted_weight_kg: parseFloat(courierForm.weight) || 1, submitted_length_cm: parseFloat(courierForm.length) || 10, submitted_width_cm: parseFloat(courierForm.width) || 10, submitted_height_cm: parseFloat(courierForm.height) || 10 },
          },
        } : {}),
        buyer_details_confirmed: confirmations.buyer_details,
        seller_details_confirmed: confirmations.seller_details,
        item_accuracy_confirmed: confirmations.item_accuracy,
      });

      const transactionId = transactionResponse.data.transaction_id;
      trackTransactionCreated({
        transaction_id: transactionId,
        value: itemPrice,
        currency: 'ZAR',
        creator_role: role,
        delivery_method: formData.delivery_method,
        fee_allocation: formData.fee_allocation,
      });
      // Photos are optional for either creator role — only attach them when some were added.
      const photoFilenames = photos.map(p => p.filename);
      if (photoFilenames.length > 0) {
        await api.patch(`/transactions/${transactionId}/photos`, photoFilenames, {
          headers: { 'Content-Type': 'application/json' },
        });
      }

      toast.success('Transaction created! Share the link with the other party.');
      navigate(`/transactions/${transactionId}`);
    } catch (error) {
      if (error.response?.data?.detail === 'EMAIL_NOT_VERIFIED') {
        setEmailVerificationRequired(true);
      } else {
        console.error('Failed to create transaction:', error);
        toast.error(parseErrorMessage(error));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 32, height: 32, border: '3px solid #242830', borderTopColor: '#2F9BFF', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  // Keep item photos optional, but available regardless of which party creates the deal.
  const isSeller = role === 'seller';
  const flowSteps = [1, 2, 3, 4];
  const stepLabels = ['Parties', 'Item Details', 'Photos', 'Confirm'];
  const currentStepIndex = flowSteps.indexOf(step);
  const goToNextStep = () => setStep(flowSteps[Math.min(currentStepIndex + 1, flowSteps.length - 1)]);
  const goToPrevStep = () => {
    if (currentStepIndex > 0) setStep(flowSteps[currentStepIndex - 1]);
    else navigate('/dashboard');
  };

  return (
    <DashboardLayout user={user} darkMain>
      <div style={S.page}>

        {!user.phone_verified && (
          <div style={{ ...S.card, borderLeft: '3px solid #F5AB1F', background: 'rgba(245,158,11,0.14)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <AlertCircle size={18} color="#F5AB1F" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#F5AB1F' }}>Verify your phone number to continue.</p>
              <p style={{ margin: 0, fontSize: 13, color: '#F5AB1F' }}>Phone verification is required before you can create a transaction or access payment actions.</p>
            </div>
            <button type="button" onClick={() => navigate('/verify/phone')} style={{ ...S.btnPrimary, width: 'auto', height: 38, padding: '0 14px', background: '#F5AB1F' }}>
              Verify phone
            </button>
          </div>
        )}

        {role === 'seller' && !hasBankingDetails && (
          <div style={{ ...S.card, borderLeft: '3px solid #F0665C', background: 'rgba(239,68,68,0.14)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <AlertCircle size={18} color="#F0665C" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#F0665C' }}>Add your banking details to receive payouts</p>
              <p style={{ margin: 0, fontSize: 13, color: '#F0665C' }}>You must add your banking details before creating a deal as a seller.</p>
            </div>
            <button type="button" onClick={() => navigate('/settings/banking')} style={{ ...S.btnPrimary, width: 'auto', height: 38, padding: '0 14px', background: '#F0665C', flexShrink: 0 }}>
              My Profile
            </button>
          </div>
        )}

        {/* Back button */}
        <button
          type="button"
          style={S.btnGhost}
          onClick={goToPrevStep}
          data-testid="back-btn"
        >
          <ArrowLeft size={14} />
          {currentStepIndex > 0 ? 'Back' : 'Dashboard'}
        </button>

        <div className="tt-wizard-heading"><span className="tt-eyebrow">New transaction · Step {currentStepIndex + 1} of {stepLabels.length}</span><h1>{stepLabels[currentStepIndex]}</h1><p>Agree the details first. Payment happens through secure checkout after both parties confirm.</p></div>
        {/* Progress stepper */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
          {stepLabels.map((label, idx) => {
            const sNum = flowSteps[idx];
            const done = currentStepIndex > idx;
            const active = step === sNum;
            return (
              <div key={label} style={{ display: 'flex', alignItems: 'center', flex: idx < stepLabels.length - 1 ? 1 : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700,
                    background: done ? '#3DD194' : active ? '#2F9BFF' : '#242830',
                    color: done || active ? '#16181B' : '#9AA4B2',
                    flexShrink: 0,
                    transition: 'all 0.2s',
                  }}>
                    {done ? <CheckCircle size={14} /> : idx + 1}
                  </div>
                  <span style={{
                    fontSize: 12,
                    fontWeight: active ? 600 : 400,
                    color: active ? '#F5F7FA' : '#9AA4B2',
                    whiteSpace: 'nowrap',
                  }} className="hidden sm:inline">
                    {label}
                  </span>
                </div>
                {idx < stepLabels.length - 1 && (
                  <div style={{
                    flex: 1, height: 2, margin: '0 10px',
                    background: currentStepIndex > idx ? '#3DD194' : '#242830',
                    transition: 'background 0.3s',
                    minWidth: 16,
                  }} />
                )}
              </div>
            );
          })}
        </div>

        <form onSubmit={handleSubmit}>

          {/* â"€â"€ Step 1: Role & Other Party â"€â"€ */}
          {step === 1 && (
            <div>
              <div style={S.card}>
                <h2 style={{ fontSize: 17, fontWeight: 700, color: '#F5F7FA', margin: '0 0 4px' }}>
                  Who are you in this transaction?
                </h2>
                <p style={{ fontSize: 13, color: '#9AA4B2', margin: '0 0 18px' }}>
                  Select your role. We'll auto-fill your details.
                </p>

                {/* Role selector */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18 }}>
                  {[
                    {
                      value: 'seller',
                      icon: Banknote,
                      iconColor: '#3DD194',
                      title: "I'm the Seller",
                      desc: "I'm selling an item and want to get paid securely",
                      testId: 'role-seller',
                    },
                    {
                      value: 'buyer',
                      icon: Shield,
                      iconColor: '#2F9BFF',
                      title: "I'm the Buyer",
                      desc: "I'm buying and want my payment protected",
                      testId: 'role-buyer',
                    },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={opt.testId}
                      onClick={() => setRole(opt.value)}
                      style={{
                        padding: '14px 14px',
                        borderRadius: 10,
                        border: `2px solid ${role === opt.value ? '#2F9BFF' : '#242830'}`,
                        background: role === opt.value ? 'rgba(47,129,244,0.08)' : '#16181B',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.15s',
                        fontFamily: 'inherit',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                        <opt.icon size={15} color={opt.iconColor} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#F5F7FA' }}>{opt.title}</span>
                      </div>
                      <p style={{ fontSize: 11, color: '#9AA4B2', margin: 0, lineHeight: 1.4 }}>{opt.desc}</p>
                    </button>
                  ))}
                </div>

                {/* Your details */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '11px 14px', borderRadius: 10,
                  background: '#101317', border: '1px solid #242830',
                  marginBottom: 20,
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #2F9BFF, #2F9BFF)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <User size={14} color="#16181B" />
                  </div>
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 600, color: '#F5F7FA', margin: 0 }}>{user.name}</p>
                    <p style={{ fontSize: 11, color: '#9AA4B2', margin: 0 }}>{user.email}</p>
                  </div>
                  <span style={{
                    marginLeft: 'auto', fontSize: 10, fontWeight: 700,
                    background: role === 'seller' ? 'rgba(16,185,129,0.14)' : 'rgba(59,130,246,0.14)',
                    color: role === 'seller' ? '#3DD194' : '#2F9BFF',
                    padding: '3px 8px', borderRadius: 20,
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>
                    {role}
                  </span>
                </div>

                {/* Other party — make the counterpart unmistakable */}
                <div style={{
                  margin: '0 0 14px',
                  padding: '12px 14px',
                  borderRadius: 10,
                  border: '1px solid rgba(47,155,255,0.35)',
                  background: 'rgba(47,155,255,0.08)',
                }}>
                  <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#2F9BFF', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Other party
                  </p>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: '#F5F7FA', margin: '4px 0 3px' }}>
                    {role === 'buyer' ? "Seller you're buying from" : "Buyer you're selling to"}
                  </h3>
                  <p style={{ margin: 0, fontSize: 12, color: '#9AA4B2', lineHeight: 1.5 }}>
                    {role === 'buyer'
                      ? "Enter the seller's details here. We'll send them a secure link to join this transaction."
                      : "Enter the buyer's details here. We'll send them a secure link to join this transaction."}
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <label style={S.label} htmlFor="other-party-name">{role === 'buyer' ? 'Seller' : 'Buyer'} Name</label>
                    <input
                      style={S.input}
                      name={role === 'buyer' ? 'seller_name' : 'buyer_name'}
                      value={role === 'buyer' ? formData.seller_name : formData.buyer_name}
                      onChange={handleChange}
                      placeholder="Full name"
                      id="other-party-name" data-testid="other-name-input"
                    />
                  </div>
                  <div>
                    <label style={S.label} htmlFor="other-party-contact">{role === 'buyer' ? 'Seller' : 'Buyer'} Email or Phone</label>
                    <input
                      style={S.input}
                      name={role === 'buyer' ? 'seller_email' : 'buyer_email'}
                      value={role === 'buyer' ? formData.seller_email : formData.buyer_email}
                      onChange={handleChange}
                      placeholder="email@example.com or +27..."
                      id="other-party-contact" data-testid="other-email-input"
                    />
                    <p style={{ fontSize: 11, color: '#9AA4B2', margin: '5px 0 0' }}>
                      They'll receive a secure link to join
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                style={{ ...S.btnPrimary, opacity: canProceedStep1 ? 1 : 0.45, cursor: canProceedStep1 ? 'pointer' : 'not-allowed' }}
                onClick={() => canProceedStep1 && setStep(2)}
                disabled={!canProceedStep1}
              >
                Continue <ArrowRight size={15} />
              </button>
            </div>
          )}

          {/* â"€â"€ Step 2: Item Details â"€â"€ */}
          {step === 2 && (
            <div>
              {/* Item basics */}
              <div style={S.card}>
                <h2 style={{ fontSize: 17, fontWeight: 700, color: '#F5F7FA', margin: '0 0 18px' }}>
                  What's being sold?
                </h2>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <label style={S.label} htmlFor="item-description">Item Description</label>
                    <textarea
                      style={S.textarea}
                      name="item_description"
                      value={formData.item_description}
                      onChange={handleChange}
                      placeholder="Describe the item in detail - model, specs, what's included..."
                      rows={3}
                      id="item-description" data-testid="item-description-input"
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                      <button
                        type="button"
                        onClick={handleImproveDescription}
                        disabled={!formData.item_description || improveLoading}
                        data-testid="improve-description-btn"
                        style={{
                          padding: '5px 12px', borderRadius: 6, border: '1px solid rgba(59,130,246,0.30)',
                          background: 'transparent', color: '#2F9BFF', fontSize: 12, fontWeight: 600,
                          cursor: !formData.item_description || improveLoading ? 'not-allowed' : 'pointer',
                          opacity: !formData.item_description || improveLoading ? 0.5 : 1,
                          fontFamily: 'inherit',
                        }}
                      >
                        {improveLoading ? 'Improving…' : '✨ Improve with AI'}
                      </button>
                    </div>
                  </div>

                  <div className="tt-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={S.label} htmlFor="item-category">Category</label>
                      <select
                        style={S.select}
                        value={formData.item_category}
                        onChange={e => setFormData(p => ({ ...p, item_category: e.target.value }))}
                        id="item-category" data-testid="item-category-select"
                      >
                        <option value="">Select...</option>
                        {ITEM_CATEGORIES.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={S.label} htmlFor="item-condition">Condition</label>
                      <select
                        style={S.select}
                        value={formData.item_condition}
                        onChange={e => setFormData(p => ({ ...p, item_condition: e.target.value }))}
                        id="item-condition" data-testid="item-condition-select"
                      >
                        <option value="">Select...</option>
                        <option value="New">New</option>
                        <option value="Used">Used</option>
                        <option value="Used - Minor Defects">Used - Minor Defects</option>
                        <option value="Used - Major Defects">Used - Major Defects</option>
                        <option value="Sold As-Is">Sold As-Is</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={S.label} htmlFor="known-issues">Known Issues (optional)</label>
                    <input
                      style={S.input}
                      id="known-issues" name="known_issues"
                      value={formData.known_issues}
                      onChange={handleChange}
                      placeholder="None - leave blank if no issues"
                    />
                  </div>

                  <div>
                    <label style={S.label} htmlFor="item-price">Price (R)</label>
                    <input
                      style={{ ...S.input, fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}
                      name="item_price"
                      type="number"
                      min={minimumTransactionAmount}
                      step="0.01"
                      value={formData.item_price}
                      onChange={handleChange}
                      placeholder="0.00"
                      aria-invalid={Boolean(amountError)}
                      id="item-price" data-testid="item-price-input"
                    />
                    <p style={{ fontSize: 11, color: amountError ? '#F0665C' : '#9AA4B2', margin: '5px 0 0' }}>
                      {getTransactionLimitCopy(minimumTransactionAmount, maximumTransactionAmount)}
                    </p>
                    {amountError && (
                      <p style={{ fontSize: 11, color: '#F0665C', margin: '4px 0 0', fontWeight: 600 }}>
                        {amountError}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Delivery Method */}
              <div style={S.card}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#F5F7FA', margin: '0 0 14px' }}>How will the item be delivered?</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    ...(COURIER_ENABLED ? [{ value: 'courier', icon: Truck, label: 'Courier Guy — we book delivery for you', desc: 'Buyer confirms receipt before payment is released.' }] : []),
                    { value: 'bank_deposit', icon: Banknote, label: 'In-Person Meeting — meet to exchange', desc: 'Payment is released once the handover is confirmed.' },
                    { value: 'digital', icon: Zap, label: 'Digital Delivery — files, codes or online services', desc: 'e.g. graphic design, software, documents. Payment is released once the digital delivery is confirmed.' },
                  ].map(opt => {
                    const active = formData.delivery_method === opt.value;
                    const disabled = !!opt.comingSoon;
                    return (
                      <label
                        key={opt.value}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '11px 14px', borderRadius: 10,
                          border: `1.5px solid ${active ? '#2F9BFF' : '#242830'}`,
                          background: active ? 'rgba(47,129,244,0.08)' : '#16181B',
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          opacity: disabled ? 0.55 : 1,
                          transition: 'all 0.15s',
                        }}
                      >
                        <input
                          type="radio"
                          name="delivery_method"
                          value={opt.value}
                          checked={active}
                          onChange={handleChange}
                          disabled={disabled}
                          className="tt-radio-input"
                        />
                        <div style={{
                          width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                          border: `2px solid ${active ? '#2F9BFF' : '#242830'}`,
                          background: active ? '#2F9BFF' : '#16181B',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.15s',
                        }}>
                          {active && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#16181B' }} />}
                        </div>
                        <opt.icon size={15} color={active ? '#2F9BFF' : '#9AA4B2'} />
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: 13, fontWeight: 500, color: '#F5F7FA' }}>{opt.label}</span>
                          {disabled && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#F5AB1F', background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 20, padding: '2px 8px', marginLeft: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Coming Soon</span>
                          )}
                          <span style={{ fontSize: 11, color: '#9AA4B2', marginLeft: 8 }}>{opt.desc}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Fee Allocation */}
              <div style={S.card}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#F5F7FA', margin: '0 0 6px' }}>Who pays the TrustTrade platform fee?</h3>
                <p style={{ fontSize: 12, color: '#9AA4B2', margin: '0 0 14px' }}>TrustTrade fee: {platformConfig.platform_fee_percent}% of the item price (minimum R{MINIMUM_PLATFORM_FEE.toFixed(0)}).</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    {
                      value: 'BUYER',
                      label: 'Buyer pays',
                      desc: itemPrice >= 100 ? describeFeePreview('BUYER') : 'Fee added to buyer payment - seller receives full item value.',
                    },
                    {
                      value: 'SELLER',
                      label: 'Seller pays',
                      desc: itemPrice >= 100 ? describeFeePreview('SELLER') : 'Fee deducted from seller payout.',
                    },
                    {
                      value: 'BUYER_SELLER',
                      label: 'Split 50/50',
                      desc: itemPrice >= 100 ? describeFeePreview('BUYER_SELLER') : 'Half from buyer, half from seller.',
                    },
                  ].map(opt => {
                    const active = feeAlloc === opt.value;
                    return (
                      <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${active ? '#2F9BFF' : '#242830'}`, background: active ? 'rgba(47,129,244,0.08)' : '#16181B', cursor: 'pointer', transition: 'all 0.15s' }}>
                        <input type="radio" name="fee_allocation" value={opt.value} checked={active} onChange={handleChange} className="tt-radio-input" />
                        <div style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, border: `2px solid ${active ? '#2F9BFF' : '#242830'}`, background: active ? '#2F9BFF' : '#16181B', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                          {active && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#16181B' }} />}
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#F5F7FA' }}>{opt.label}</span>
                          <span style={{ fontSize: 11, color: '#9AA4B2', marginLeft: 8 }}>{opt.desc}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Courier Guy Delivery — shown automatically when Courier Guy is the chosen delivery method */}
              {COURIER_ENABLED && isCourierDelivery && (
                <div style={S.card}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20, padding: '12px 14px', background: 'rgba(59,130,246,0.14)', border: '1px solid rgba(59,130,246,0.30)', borderRadius: 10 }}>
                    <Truck size={16} color="#2F9BFF" style={{ flexShrink: 0, marginTop: 1 }} />
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#2F9BFF', display: 'block', marginBottom: 2 }}>TrustTrade books the courier for you</span>
                      <span style={{ fontSize: 12, color: '#2F9BFF', lineHeight: 1.5 }}>
                        Enter the pickup and delivery addresses below and we'll arrange the Courier Guy shipment on your behalf —
                        the delivery cost is added to this transaction and stays protected with the payment. You don't need to book or pay the courier separately.
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                      {/* Collection preference */}
                      <div>
                        <label style={S.label}>How will the parcel reach Courier Guy?</label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                          {[
                            { value: 'collection', label: 'Collection', desc: 'Courier Guy collects from the seller’s address.' },
                            { value: 'dropoff', label: 'Drop-off', desc: 'Seller drops it at a Courier Guy drop-off point.' },
                          ].map(opt => {
                            const active = courierForm.collection_preference === opt.value;
                            return (
                              <label key={opt.value} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${active ? '#2F9BFF' : '#242830'}`, background: active ? 'rgba(47,129,244,0.08)' : '#16181B', cursor: 'pointer', transition: 'all 0.15s' }}>
                                <input type="radio" name="collection_preference" value={opt.value} checked={active} onChange={e => setCourierForm(p => ({ ...p, collection_preference: e.target.value }))} className="tt-radio-input" />
                                <div style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 1, border: `2px solid ${active ? '#2F9BFF' : '#242830'}`, background: active ? '#2F9BFF' : '#16181B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {active && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#16181B' }} />}
                                </div>
                                <div>
                                  <span style={{ fontSize: 13, fontWeight: 600, color: '#F5F7FA', display: 'block' }}>{opt.label}</span>
                                  <span style={{ fontSize: 11, color: '#9AA4B2' }}>{opt.desc}</span>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>

                      {/* Pickup */}
                      <div>
                        <label style={S.label}>{isDropoff ? 'Seller’s Area (for pricing the drop-off)' : 'Pickup Address (Seller)'}</label>
                        {isDropoff && (
                          <p style={{ fontSize: 11, color: '#9AA4B2', margin: '0 0 8px' }}>
                            No street address needed — the seller takes the parcel to a Courier Guy point. We just need the area and postal code to price the route.
                          </p>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {!isDropoff && (
                            <input style={S.input} placeholder="Street address" value={courierForm.pickup_street} onChange={e => setCourierForm(p => ({ ...p, pickup_street: e.target.value }))} />
                          )}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                            <input style={S.input} placeholder="Suburb / area" value={courierForm.pickup_area} onChange={e => setCourierForm(p => ({ ...p, pickup_area: e.target.value }))} />
                            <input style={S.input} placeholder="City" value={courierForm.pickup_city} onChange={e => setCourierForm(p => ({ ...p, pickup_city: e.target.value }))} />
                          </div>
                          <input style={{ ...S.input, maxWidth: 160 }} placeholder="Postal code" value={courierForm.pickup_code} onChange={e => setCourierForm(p => ({ ...p, pickup_code: e.target.value }))} />
                        </div>
                      </div>

                      {/* Delivery */}
                      <div>
                        <label style={S.label}>Delivery Address (Buyer)</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <input style={S.input} placeholder="Street address" value={courierForm.delivery_street} onChange={e => setCourierForm(p => ({ ...p, delivery_street: e.target.value }))} />
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                            <input style={S.input} placeholder="Suburb / area" value={courierForm.delivery_area} onChange={e => setCourierForm(p => ({ ...p, delivery_area: e.target.value }))} />
                            <input style={S.input} placeholder="City" value={courierForm.delivery_city} onChange={e => setCourierForm(p => ({ ...p, delivery_city: e.target.value }))} />
                          </div>
                          <input style={{ ...S.input, maxWidth: 160 }} placeholder="Postal code" value={courierForm.delivery_code} onChange={e => setCourierForm(p => ({ ...p, delivery_code: e.target.value }))} />
                        </div>
                      </div>

                      {/* Parcel dimensions */}
                      <div>
                        <label style={S.label}>Parcel Details</label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: 8 }}>
                          {[
                            { label: 'Weight (kg)', key: 'weight' },
                            { label: 'Length (cm)', key: 'length' },
                            { label: 'Width (cm)', key: 'width' },
                            { label: 'Height (cm)', key: 'height' },
                          ].map(f => (
                            <div key={f.key}>
                              <label style={{ ...S.label, fontSize: 10, marginBottom: 4 }}>{f.label}</label>
                              <input style={S.input} type="number" min="0.1" step="0.1" value={courierForm[f.key]} onChange={e => setCourierForm(p => ({ ...p, [f.key]: e.target.value }))} />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Get Quote button */}
                      <button
                        type="button"
                        onClick={handleGetQuote}
                        disabled={courierLoading || (!isDropoff && !courierForm.pickup_street) || !courierForm.pickup_city || !courierForm.pickup_code || !courierForm.delivery_street || !courierForm.delivery_city || !courierForm.delivery_code}
                        style={{
                          alignSelf: 'flex-start', padding: '9px 18px', borderRadius: 8, border: 'none',
                          background: '#2F9BFF', color: '#061421', fontSize: 13, fontWeight: 600,
                          cursor: courierLoading ? 'not-allowed' : 'pointer',
                          opacity: (courierLoading || (!isDropoff && !courierForm.pickup_street) || !courierForm.pickup_city || !courierForm.pickup_code || !courierForm.delivery_street || !courierForm.delivery_city || !courierForm.delivery_code) ? 0.5 : 1,
                          display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'inherit',
                        }}
                      >
                        {courierLoading
                          ? <><Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> Getting quotes…</>
                          : <><Truck size={13} /> Get Quote</>}
                      </button>

                      {/* Quote results */}
                      {courierQuotes.length > 0 && (
                        <div>
                          <label style={S.label}>Select a delivery option</label>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                            {courierQuotes.map((q, i) => {
                              const price = q?.price ?? q?.rate ?? 0;
                              const name = q?.service_level?.name ?? q?.name ?? `Option ${i + 1}`;
                              const days = q?.service_level?.delivery_days ?? q?.delivery_days;
                              const levelDesc = COURIER_LEVEL_DESCRIPTIONS[String(name).trim().toLowerCase()];
                              const isSelected = selectedQuote === q;
                              const chargedWeight = q?.charged_weight;
                              const actualWeight = q?.actual_weight;
                              const isVolumetric = chargedWeight && actualWeight && chargedWeight > actualWeight;
                              return (
                                <label key={i} style={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                  padding: '11px 14px', borderRadius: 10, cursor: 'pointer',
                                  border: `1.5px solid ${isSelected ? '#2F9BFF' : '#242830'}`,
                                  background: isSelected ? 'rgba(47,129,244,0.08)' : '#16181B',
                                  transition: 'all 0.15s',
                                }}>
                                  <input type="radio" checked={isSelected} onChange={() => setSelectedQuote(q)} className="tt-radio-input" />
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                                    <div style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, border: `2px solid ${isSelected ? '#2F9BFF' : '#242830'}`, background: isSelected ? '#2F9BFF' : '#16181B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      {isSelected && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#16181B' }} />}
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                      <span style={{ fontSize: 13, fontWeight: 500, color: '#F5F7FA', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                                      {levelDesc
                                        ? <span style={{ fontSize: 11, color: '#9AA4B2', display: 'block', lineHeight: 1.4 }}>{levelDesc}</span>
                                        : (days && <span style={{ fontSize: 11, color: '#9AA4B2' }}>{days} business day{days !== 1 ? 's' : ''}</span>)}
                                    </div>
                                  </div>
                                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: '#3DD194', fontFamily: 'ui-monospace, monospace', display: 'block' }}>
                                      R {Number(price).toFixed(2)}
                                    </span>
                                    {isVolumetric && (
                                      <span style={{ fontSize: 10, color: '#9AA4B2', whiteSpace: 'nowrap' }}>
                                        Volumetric: {chargedWeight}kg
                                      </span>
                                    )}
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                </div>
              )}

              {/* Price breakdown */}
              {itemPrice >= 100 && (
                <div style={{
                  background: '#101317',
                  border: '1px solid #242830',
                  borderRadius: 12, padding: '16px 20px', marginBottom: 14,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: '#9AA4B2' }}>Item value</span>
                    <span style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace', color: '#F5F7FA' }}>
                      R {itemPrice.toFixed(2)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: courierFee > 0 ? 8 : 12 }}>
                    <span style={{ fontSize: 12, color: '#9AA4B2' }}>
                      Buyer TrustTrade Fee
                    </span>
                    <span style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace', color: '#9AA4B2' }}>
                      R {buyerFeeContrib.toFixed(2)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: courierFee > 0 ? 8 : 12 }}>
                    <span style={{ fontSize: 12, color: '#9AA4B2' }}>
                      Seller TrustTrade Fee
                    </span>
                    <span style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace', color: '#9AA4B2' }}>
                      R {sellerFeeContrib.toFixed(2)}
                    </span>
                  </div>
                  {buyerHasProviderFee && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: courierFee > 0 ? 8 : 12 }}>
                      <span style={{ fontSize: 12, color: '#9AA4B2' }}>Buyer payment processing</span>
                      <span style={{ fontSize: 12, color: '#9AA4B2' }}>Shown on the payment screen before you pay</span>
                    </div>
                  )}
                  {sellerHasProviderFee && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: courierFee > 0 ? 8 : 12 }}>
                      <span style={{ fontSize: 12, color: '#9AA4B2' }}>Seller payment processing</span>
                      <span style={{ fontSize: 12, color: '#9AA4B2' }}>Confirmed by provider at payout</span>
                    </div>
                  )}
                  {courierFee > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                      <span style={{ fontSize: 12, color: '#9AA4B2' }}>Courier Delivery</span>
                      <span style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace', color: '#9AA4B2' }}>
                        + R {courierFee.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, borderTop: '1px solid #242830', marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#F5F7FA' }}>{buyerHasProviderFee ? 'Total before provider fee' : 'Buyer Pays Total'}</span>
                    <span style={{ fontSize: 15, fontWeight: 700, fontFamily: 'ui-monospace, monospace', color: '#2F9BFF' }}>
                      R {buyerPaysBeforeProvider.toFixed(2)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
                    <span style={{ fontSize: 12, color: '#9AA4B2' }}>{sellerHasProviderFee ? 'Seller Receives (up to)' : 'Seller Receives'}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, fontFamily: 'ui-monospace, monospace', color: '#3DD194' }}>
                      R {sellerReceivesBeforeProvider.toFixed(2)}
                    </span>
                  </div>
                  <p style={{ fontSize: 10, color: '#9AA4B2', margin: '10px 0 0', lineHeight: 1.5 }}>
                    {feeAlloc === 'BUYER' && 'TrustTrade\'s platform fee is included in the buyer\'s payment. Seller receives the full item value.'}
                    {feeAlloc === 'SELLER' && 'TrustTrade\'s platform fee is deducted from the seller\'s payout. Buyer pays the item price only.'}
                    {feeAlloc === 'BUYER_SELLER' && 'TrustTrade\'s platform fee is split equally — half from buyer, half from seller payout.'}
                    {' '}The full breakdown, including TradeSafe’s processing fee and VAT, is shown on the payment screen before you pay.
                  </p>
                </div>
              )}

              <button
                type="button"
                style={{ ...S.btnPrimary, opacity: canProceedStep2 ? 1 : 0.45, cursor: canProceedStep2 ? 'pointer' : 'not-allowed' }}
                onClick={() => canProceedStep2 && goToNextStep()}
                disabled={!canProceedStep2}
              >
                Continue <ArrowRight size={15} />
              </button>
            </div>
          )}

          {/* â"€â"€ Step 3: Item photos (optional for either creator role) â"€â"€ */}
          {step === 3 && (
            <div>
              <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(59,130,246,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Camera size={16} color="#2F9BFF" />
                  </div>
                  <div>
                    <h2 style={{ fontSize: 17, fontWeight: 700, color: '#F5F7FA', margin: 0 }}>Add Item Photos <span style={{ fontSize: 13, fontWeight: 500, color: '#F5AB1F' }}>(optional)</span></h2>
                    <p style={{ fontSize: 12, color: '#9AA4B2', margin: 0 }}>Photos create a clearer record of what both sides agreed to</p>
                  </div>
                </div>

                <div style={{
                  background: 'rgba(59,130,246,0.14)', border: '1px solid rgba(59,130,246,0.30)',
                  borderRadius: 10, padding: '10px 14px', marginBottom: 16, marginTop: 14,
                }}>
                  <p style={{ fontSize: 12, color: '#2F9BFF', margin: 0 }}>
                    {isSeller
                      ? <>Add up to <strong>5 clear photos</strong> of the item, including any defects.</>
                      : <>Add up to <strong>5 listing photos or screenshots</strong> that show the item you are buying.</>}
                    {' '}Photos are optional, but recommended.
                  </p>
                </div>

                <PhotoUploader photos={photos} setPhotos={setPhotos} minPhotos={0} maxPhotos={5} required={false} />
              </div>

              <button
                type="button"
                style={S.btnPrimary}
                onClick={goToNextStep}
              >
                Continue <ArrowRight size={15} />
              </button>
            </div>
          )}

          {/* â"€â"€ Step 4: Confirm â"€â"€ */}
          {step === 4 && (
            <div>
              {emailVerificationRequired && <EmailVerificationPrompt />}
              {/* Summary */}
              <div style={S.card}>
                <h2 style={{ fontSize: 17, fontWeight: 700, color: '#F5F7FA', margin: '0 0 18px' }}>Review & Confirm</h2>

                {/* Parties side by side */}
                <div className="tt-party-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                  {[
                    { roleLabel: 'Buyer', name: role === 'buyer' ? user.name : formData.buyer_name, email: role === 'buyer' ? user.email : formData.buyer_email, accent: '#2F9BFF', bg: 'rgba(59,130,246,0.14)', isYou: role === 'buyer' },
                    { roleLabel: 'Seller', name: role === 'seller' ? user.name : formData.seller_name, email: role === 'seller' ? user.email : formData.seller_email, accent: '#3DD194', bg: 'rgba(16,185,129,0.14)', isYou: role === 'seller' },
                  ].map(p => (
                    <div key={p.roleLabel} style={{ background: p.bg, border: `1px solid ${p.accent}33`, borderRadius: 10, padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: p.accent }}>{p.roleLabel}</span>
                        {p.isYou && <span style={{ fontSize: 9, fontWeight: 600, background: p.accent, color: '#061421', padding: '1px 5px', borderRadius: 20 }}>You</span>}
                      </div>
                      <p style={{ margin: '0 0 2px', fontSize: 13, fontWeight: 600, color: '#F5F7FA', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name || '—'}</p>
                      <p style={{ margin: 0, fontSize: 11, color: '#9AA4B2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.email || '—'}</p>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {[
                    { label: 'Item', value: formData.item_description, truncate: true },
                    { label: 'Item Value', value: `R ${itemPrice.toFixed(2)}`, mono: true },
                    ...(courierFee > 0 ? [
                      { label: 'Courier Delivery', value: `R ${courierFee.toFixed(2)}`, mono: true },
                    ] : []),
                    { label: 'Buyer TrustTrade Fee', value: `R ${buyerFeeContrib.toFixed(2)}`, mono: true },
                    { label: 'Seller TrustTrade Fee', value: `R ${sellerFeeContrib.toFixed(2)}`, mono: true },
                    ...(buyerHasProviderFee ? [{ label: 'Buyer payment processing', value: 'Shown on the payment screen before you pay' }] : []),
                    ...(sellerHasProviderFee ? [{ label: 'Seller payment processing', value: 'Confirmed by provider at payout' }] : []),
                    { label: buyerHasProviderFee ? 'Total before provider fee' : 'Buyer Pays Total', value: `R ${buyerPaysBeforeProvider.toFixed(2)}`, mono: true, accent: '#2F9BFF' },
                    { label: sellerHasProviderFee ? 'Seller Receives (up to)' : 'Seller Receives', value: `R ${sellerReceivesBeforeProvider.toFixed(2)}`, mono: true, accent: '#3DD194' },
                    { label: 'Photos', value: photos.length > 0 ? `${photos.length} uploaded` : 'None (optional)' },
                    { label: 'Delivery', value: DELIVERY_METHOD_LABELS[formData.delivery_method] || formData.delivery_method.replaceAll('_', ' ') },
                  ].map((row, i, arr) => (
                    <div key={row.label} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 0',
                      borderBottom: i < arr.length - 1 ? '1px solid #242830' : 'none',
                    }}>
                      <span style={{ fontSize: 13, color: '#9AA4B2' }}>{row.label}</span>
                      <span style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: row.accent || '#F5F7FA',
                        fontFamily: row.mono ? 'ui-monospace, monospace' : 'inherit',
                        maxWidth: 220,
                        overflow: 'hidden',
                        textOverflow: row.truncate ? 'ellipsis' : undefined,
                        whiteSpace: row.truncate ? 'nowrap' : undefined,
                        textTransform: row.label === 'Delivery' ? 'capitalize' : undefined,
                      }}>
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: 12, color: '#9AA4B2', margin: '12px 0 0' }}>
                  The full breakdown, including TradeSafe’s processing fee and VAT, is shown on the payment screen before you pay.
                </p>
              </div>

              {/* Confirmations */}
              <div style={S.card}>
                <h3 style={{ fontSize: 13, fontWeight: 600, color: '#F5F7FA', margin: '0 0 14px' }}>
                  Please confirm all of the following:
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {[
                    { key: 'buyer_details', label: 'Buyer details are accurate', testId: 'confirm-buyer-details' },
                    { key: 'seller_details', label: 'Seller details are accurate', testId: 'confirm-seller-details' },
                    { key: 'item_accuracy', label: 'Item details are accurate and complete', testId: 'confirm-item-accuracy' },
                  ].map(item => {
                    const checked = confirmations[item.key];
                    return (
                      <label
                        key={item.key}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                        data-testid={item.testId}
                        onClick={() => setConfirmations(p => ({ ...p, [item.key]: !p[item.key] }))}
                      >
                        <div style={{
                          width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                          border: `2px solid ${checked ? '#3DD194' : '#242830'}`,
                          background: checked ? '#3DD194' : '#16181B',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.15s',
                        }}>
                          {checked && <Check size={11} color="#16181B" strokeWidth={3} />}
                        </div>
                        <span style={{ fontSize: 13, color: '#F5F7FA' }}>{item.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <button
                type="submit"
                style={{
                  ...S.btnSuccess,
                  opacity: (loading || !confirmations.buyer_details || !confirmations.seller_details || !confirmations.item_accuracy) ? 0.5 : 1,
                  cursor: (loading || !confirmations.buyer_details || !confirmations.seller_details || !confirmations.item_accuracy) ? 'not-allowed' : 'pointer',
                }}
                disabled={loading || !confirmations.buyer_details || !confirmations.seller_details || !confirmations.item_accuracy}
                data-testid="create-transaction-btn"
              >
                {loading ? (
                  <>
                    <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#16181B', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Creating...
                  </>
                ) : (
                  <>
                    <Shield size={15} />
                    Create protected transaction
                  </>
                )}
              </button>
            </div>
          )}
        </form>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input:focus, textarea:focus, select:focus { border-color: #2F9BFF !important; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
        @media (max-width: 480px) { .tt-party-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </DashboardLayout>
  );
}

export default NewTransaction;
